import {css, StyleSheet} from 'aphrodite';
import PropTypes from 'prop-types';
import React from 'react';
import AceEditor from 'react-ace';

import BenchlingConfig from 'BenchlingConfig';
import * as RegistryConstants from 'constants/RegistryConstants';
import {LABEL_TEMPLATE_SHAPE, LabelTemplate} from 'constants/SampleConstants';
import {generateLabelTemplatePreview} from 'settings/label-printing/LabelTemplateSettingsDAO';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import SettingsField, {SettingsLabel} from 'ui-library/SettingsField';
import {ElementMouseEvent} from 'util/Types';

const {SAVING_STATES} = RegistryConstants;

type LabelTemplateContent = Pick<LabelTemplate, 'name' | 'zplTemplate'>;

export interface LabelTemplateSettingsProps {
  isBeingCreated: boolean;
  isWritable: boolean;
  onCreate: (labelTemplateContent: LabelTemplateContent) => void;
  onUpdate: (labelTemplateContent: LabelTemplateContent) => void;
  labelTemplate?: LabelTemplate;
  savingState: string;
}

interface LabelTemplateSettingsState {
  labelTemplateContent: LabelTemplateContent;
  base64Preview: string | null;
  altText: string;
  generatingPreview: boolean;
}

export default class LabelTemplateSettings extends React.PureComponent<
  LabelTemplateSettingsProps,
  LabelTemplateSettingsState
> {
  static propTypes = {
    isBeingCreated: PropTypes.bool.isRequired,
    isWritable: PropTypes.bool.isRequired,
    onCreate: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    labelTemplate: LABEL_TEMPLATE_SHAPE,
    savingState: PropTypes.oneOf(Object.keys(SAVING_STATES)).isRequired,
  };

  constructor(props: LabelTemplateSettingsProps) {
    super(props);
    let labelTemplateContent;
    if (this.props.labelTemplate) {
      labelTemplateContent = this.props.labelTemplate;
    } else {
      labelTemplateContent = {
        name: '',
        zplTemplate: '',
      };
    }
    this.state = {
      labelTemplateContent,
      base64Preview: null,
      altText: 'Refresh preview to view label template',
      generatingPreview: false,
    };
  }

  override UNSAFE_componentWillReceiveProps = (nextProps: LabelTemplateSettingsProps): void => {
    if (nextProps.labelTemplate) {
      this.setState({labelTemplateContent: nextProps.labelTemplate});
    }
  };

  override componentDidUpdate = (
    prevProps: LabelTemplateSettingsProps,
    prevState: LabelTemplateSettingsState
  ): void => {
    const wasSaving = prevProps.savingState === SAVING_STATES.PENDING;
    const isSaving = this.props.savingState === SAVING_STATES.PENDING;
    if (!wasSaving && isSaving) {
      // Saving started
      LoadingScreen.show('Saving...');
    } else if (wasSaving && !isSaving) {
      // Saving finished
      LoadingScreen.hide();
    }
  };

  _handleSubmit = (
    e: ElementMouseEvent | null,
    labelTemplate: LabelTemplateContent = this.state.labelTemplateContent
  ): void => {
    if (this.props.isBeingCreated) {
      this.props.onCreate(labelTemplate);
      return;
    }
    this.props.onUpdate(labelTemplate);
  };

  _handleChangeName = (name: string): void => {
    const labelTemplateContent = {...this.state.labelTemplateContent, name};
    this.setState({labelTemplateContent});
    if (!this.props.isBeingCreated) {
      this._handleSubmit(null, labelTemplateContent);
    }
  };

  _handleChangeZplTemplate = (zplTemplate: string): void => {
    this.setState({labelTemplateContent: {...this.state.labelTemplateContent, zplTemplate}});
  };

  _updateZplTemplatePreview = (): void => {
    this.setState({generatingPreview: true});
    generateLabelTemplatePreview(this.state.labelTemplateContent.zplTemplate)
      .done((response) => {
        this.setState({base64Preview: response!.data, generatingPreview: false});
      })
      .fail((res) => {
        this.setState({base64Preview: null, altText: res.responseJSON.userMessage, generatingPreview: false});
      });
  };

  _getFirstColumnSize = (): number => (BenchlingConfig.INVENTORY_ENABLE_ZPL_LABEL_PREVIEW ? 6 : 12);

  override render(): JSX.Element {
    return (
      <div className="u-flex-grow labelTemplateSettings">
        <div className="settingsPageContainer-section">
          <Row>
            <Col sm={12}>
              <SettingsField
                isBeingCreated={this.props.isBeingCreated}
                isRequired={true}
                isWritable={this.props.isWritable}
                label="Name"
                name="name"
                onChange={this._handleChangeName}
                value={this.state.labelTemplateContent.name}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={this._getFirstColumnSize()}>
              <SettingsLabel isRequired={true} label="ZPL Template" name="zplTemplate" />
              <AceEditor
                enableBasicAutocompletion={false}
                onChange={this._handleChangeZplTemplate}
                readOnly={!this.props.isWritable}
                theme="github"
                value={this.state.labelTemplateContent.zplTemplate}
                width="auto"
                height={HEIGHT}
              />
            </Col>
            {BenchlingConfig.INVENTORY_ENABLE_ZPL_LABEL_PREVIEW && (
              <Col sm={6} className={css(styles.specificHeight)}>
                <SettingsLabel isRequired={false} label="Preview" name="preview" />
                <ImagePreview
                  base64Preview={this.state.base64Preview}
                  altText={this.state.altText}
                  generatingPreview={this.state.generatingPreview}
                />
              </Col>
            )}
          </Row>
          <Row>
            <Col sm={this._getFirstColumnSize()}>
              {!this.props.isBeingCreated && (
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  className="u-tiny-spacing-top"
                  disabled={!this.props.isWritable}
                  onClick={this._handleSubmit}
                  size={ButtonSize.MEDIUM}
                  text="Update ZPL Template"
                />
              )}
            </Col>
            {BenchlingConfig.INVENTORY_ENABLE_ZPL_LABEL_PREVIEW && (
              <Col sm={6}>
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  className="u-tiny-spacing-top"
                  onClick={this._updateZplTemplatePreview}
                  size={ButtonSize.MEDIUM}
                  text="Refresh Preview"
                  disabled={this.state.generatingPreview}
                />
              </Col>
            )}
          </Row>
        </div>
        {this.props.isBeingCreated && (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            disabled={!this.props.isWritable}
            onClick={this._handleSubmit}
            size={ButtonSize.MEDIUM}
            text="Create"
          />
        )}
      </div>
    );
  }
}

const HEIGHT = '500px';

const styles = StyleSheet.create({
  specificHeight: {
    height: HEIGHT,
  },
  maxWidthFillHeight: {
    maxWidth: '100%',
    height: '100%',
  },
});

interface ImagePreviewProps {
  base64Preview: string | null;
  altText: string;
  generatingPreview: boolean;
}

function ImagePreview(props: ImagePreviewProps): JSX.Element {
  const _renderImagePreview = (): JSX.Element =>
    props.base64Preview ? (
      <img className={css(styles.maxWidthFillHeight)} src={`data:image/png;base64,${props.base64Preview}`} />
    ) : (
      <span>{props.altText}</span>
    );
  return props.generatingPreview ? <CenteredHorizontalThrobber /> : _renderImagePreview();
}
